export type TuningCategory =
  | 'standard'
  | 'drop'
  | 'open'
  | 'modal'
  | 'other-instrument';

export interface Tuning {
  id: string;
  name: string;
  category: TuningCategory;
  /** MIDI numbers, ordered low pitch index → high pitch index. Length = string count. */
  midi: number[];
  /** True if chord library can't help with this tuning. Scales still work. */
  noChords?: boolean;
}

export const MAX_STRINGS = 6;

// MIDI reference: C4 = 60, A4 = 69, E2 = 40.
// All numbers below low-to-high pitch.
export const TUNINGS: Tuning[] = [
  // Standard variants
  { id: 'standard',        name: 'Standard',           category: 'standard', midi: [40, 45, 50, 55, 59, 64] },  // E2 A2 D3 G3 B3 E4
  { id: 'half-step-down',  name: 'Half Step Down',     category: 'standard', midi: [39, 44, 49, 54, 58, 63] },  // Eb2 Ab2 Db3 Gb3 Bb3 Eb4
  { id: 'full-step-down',  name: 'Full Step Down',     category: 'standard', midi: [38, 43, 48, 53, 57, 62] },  // D2 G2 C3 F3 A3 D4
  { id: 'c-standard',      name: 'C Standard',         category: 'standard', midi: [36, 41, 46, 51, 55, 60] },  // C2 F2 Bb2 Eb3 G3 C4

  // Drop tunings
  { id: 'drop-d',          name: 'Drop D',             category: 'drop',     midi: [38, 45, 50, 55, 59, 64] },  // D2 A2 D3 G3 B3 E4
  { id: 'drop-c',          name: 'Drop C',             category: 'drop',     midi: [36, 43, 48, 53, 57, 62] },  // C2 G2 C3 F3 A3 D4
  { id: 'drop-b',          name: 'Drop B',             category: 'drop',     midi: [35, 42, 47, 52, 56, 61] },  // B1 F#2 B2 E3 G#3 C#4
  { id: 'drop-a',          name: 'Drop A',             category: 'drop',     midi: [33, 40, 45, 50, 54, 59] },  // A1 E2 A2 D3 F#3 B3
  { id: 'double-drop-d',   name: 'Double Drop D',      category: 'drop',     midi: [38, 45, 50, 55, 59, 62] },  // D2 A2 D3 G3 B3 D4

  // Open tunings
  { id: 'open-d',          name: 'Open D',             category: 'open',     midi: [38, 45, 50, 54, 57, 62] },  // D2 A2 D3 F#3 A3 D4
  { id: 'open-g',          name: 'Open G',             category: 'open',     midi: [38, 43, 50, 55, 59, 62] },  // D2 G2 D3 G3 B3 D4
  { id: 'open-e',          name: 'Open E',             category: 'open',     midi: [40, 47, 52, 56, 59, 64] },  // E2 B2 E3 G#3 B3 E4
  { id: 'open-a',          name: 'Open A',             category: 'open',     midi: [40, 45, 49, 52, 57, 64] },  // E2 A2 C#3 E3 A3 E4
  { id: 'open-c',          name: 'Open C',             category: 'open',     midi: [36, 43, 48, 55, 60, 64] },  // C2 G2 C3 G3 C4 E4

  // Modal / Folk
  { id: 'dadgad',          name: 'DADGAD',             category: 'modal',    midi: [38, 45, 50, 55, 57, 62] },  // D2 A2 D3 G3 A3 D4

  // Other instruments
  { id: 'bass-guitar',     name: 'Bass Guitar (4-string)', category: 'other-instrument', midi: [28, 33, 38, 43], noChords: true },  // E1 A1 D2 G2
  { id: 'ukulele',         name: 'Ukulele (high-G)',   category: 'other-instrument', midi: [67, 60, 64, 69], noChords: true },  // G4 C4 E4 A4 (reentrant)
  { id: 'violin',          name: 'Violin (Standard)',  category: 'other-instrument', midi: [55, 62, 69, 76], noChords: true },  // G3 D4 A4 E5
];

export const STANDARD_TUNING = TUNINGS[0];

export function getTuningById(id: string): Tuning {
  return TUNINGS.find(t => t.id === id) ?? STANDARD_TUNING;
}

/**
 * Whether this tuning's labels should be displayed with sharps (vs flats).
 * Flat-key root tunings (C, F, Bb, Eb, Ab, Db) → flats; everything else → sharps.
 */
export function tuningPrefersSharps(tuning: Tuning): boolean {
  const lowestSemi = ((tuning.midi[0] % 12) + 12) % 12;
  // Pitch classes that are "flat-key roots": C(0), F(5), A#/Bb(10), D#/Eb(3), G#/Ab(8), C#/Db(1)
  const FLAT_KEY_PCS = new Set([0, 5, 10, 3, 8, 1]);
  return !FLAT_KEY_PCS.has(lowestSemi);
}
